import { useState } from "react";
import { ImageOff } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

/**
 * StepScreenshot — renders a step's screenshot with graceful fallback
 * to a "Screenshot expired" placeholder when the image fails to load.
 *
 * Iter 38 context: Screenshots used to be stored on the pod's
 * ephemeral disk (`/app/backend/artifacts/`). Every pod restart wiped
 * them, leaving Mongo `runs.step_results[i].screenshot` URLs pointing
 * at files that no longer existed → the browser rendered its default
 * "broken image icon". New runs (post-Iter-38) persist screenshots to
 * Emergent object storage; but old runs' images are gone forever,
 * so we render a clean placeholder instead of the ugly broken icon.
 *
 * Two rendering modes:
 *   - `variant="thumbnail"` (default) — button that opens a zoom
 *     dialog on click. Used in the step timeline.
 *   - `variant="zoom"` — full-size image inside the zoom dialog.
 *
 * When `variant="thumbnail"` and `onZoom` is supplied, clicking the
 * screenshot invokes onZoom(step). If the image failed to load, the
 * button becomes non-interactive + shows the placeholder inline.
 */
export function StepScreenshot({
  step,
  onZoom,
  variant = "thumbnail",
  className = "",
  testId,
}) {
  const [errored, setErrored] = useState(false);

  if (!step?.screenshot) return null;

  const src = `${BACKEND_URL}${step.screenshot}`;

  if (variant === "zoom") {
    if (errored) {
      return (
        <ExpiredPlaceholder
          variant="zoom"
          className={className}
          testId={testId}
        />
      );
    }
    return (
      <img
        src={src}
        alt={`Step ${step.index + 1} full`}
        className={className || "w-full rounded-md"}
        data-testid={testId || "zoomed-screenshot"}
        onError={() => setErrored(true)}
      />
    );
  }

  // Thumbnail variant.
  if (errored) {
    return (
      <ExpiredPlaceholder
        variant="thumbnail"
        className={className}
        testId={testId || `step-screenshot-expired-${step.index}`}
      />
    );
  }
  return (
    <button
      onClick={() => onZoom && onZoom(step)}
      data-testid={testId || `step-screenshot-${step.index}`}
      className={
        className ||
        "rounded-md border border-[#E5E7EB] hover:border-[#3B82F6] hover:shadow-md transition-all h-fit overflow-hidden"
      }
    >
      <img
        src={src}
        alt={`Step ${step.index + 1}`}
        className="w-full object-cover"
        loading="lazy"
        onError={() => setErrored(true)}
      />
    </button>
  );
}

function ExpiredPlaceholder({ variant, className, testId }) {
  const isZoom = variant === "zoom";
  return (
    <div
      data-testid={testId}
      className={
        className ||
        `flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[#E5E7EB] bg-[#F8FAFC] text-[#64748B] ${
          isZoom ? "p-16 min-h-[280px]" : "p-4 min-h-[110px] w-[180px]"
        }`
      }
      title="This run's screenshots are no longer available on the server."
    >
      <ImageOff className={isZoom ? "w-10 h-10" : "w-6 h-6"} aria-hidden />
      <div
        className={
          isZoom
            ? "text-sm font-medium text-center"
            : "text-[11px] font-medium text-center leading-tight"
        }
      >
        Screenshot expired
      </div>
      {isZoom && (
        <div className="text-xs text-[#94A3B8] max-w-[380px] text-center leading-relaxed">
          This run&apos;s screenshot is no longer stored on the server.
          Re-run the test to capture a fresh screenshot.
        </div>
      )}
    </div>
  );
}
